import type {
  RegionNameFragment,
  RegionTypeFragment,
} from "../client/generated";
import type { PossibleTypesResultData } from "../client/generated/introspection";
import { i18next } from "../i18n";

export type AttributeKind =
  PossibleTypesResultData["possibleTypes"]["Attribute"][number];

export type RegionType = MatchRegionTypeResult[0];

type MatchRegionTypeResult =
  | ["artifact", { paginationId: string }]
  | ["caption", { captionId: string; textId: string }]
  | ["formula", { expressionId: string }]
  | ["figure", { figureId: string }]
  | ["heading", { headerLevelId: string; textId: string }]
  | ["image", { alternativeTextId: string; imageId: string }]
  | ["list", { listId: string }]
  | ["listItem", { listItemId: string; textId: string }]
  | ["sublist", { listId: string; listItemId: string }]
  | ["table", { tableId: string }]
  | ["toc", { tocId: string }]
  | ["text", { textId: string }]
  | ["untyped", Record<string, never>]
  | ["hybrid", Record<string, never>];

export function matchRegionType(
  region: RegionTypeFragment,
): MatchRegionTypeResult {
  const numAttributes = region.attributes.length;

  if (numAttributes === 0) {
    return ["untyped", {}];
  }

  const kind2id = region.attributes.reduce(
    (acc, attr) => {
      acc[attr.__typename] = attr.id;
      return acc;
    },
    {} as Record<AttributeKind, string | undefined>,
  );

  const {
    AlternativeTextAttribute: alternativeTextId,
    CaptionAttribute: captionId,
    ExpressionAttribute: expressionId,
    FigureAttribute: figureId,
    HeaderLevelAttribute: headerLevelId,
    ImageAttribute: imageId,
    ListAttribute: listId,
    ListItemAttribute: listItemId,
    PaginationArtifactAttribute: paginationId,
    TableAttribute: tableId,
    TableOfContentsAttribute: tocId,
    TextAttribute: textId,
  } = kind2id;

  if (paginationId && numAttributes === 1) {
    return ["artifact", { paginationId }];
  }

  if (captionId && textId && numAttributes === 2) {
    return ["caption", { captionId, textId }];
  }

  if (expressionId && numAttributes === 1) {
    return ["formula", { expressionId }];
  }

  if (figureId && numAttributes === 1) {
    return ["figure", { figureId }];
  }

  if (headerLevelId && textId && numAttributes === 2) {
    return ["heading", { headerLevelId, textId }];
  }

  if (alternativeTextId && imageId && numAttributes === 2) {
    return ["image", { alternativeTextId, imageId }];
  }

  if (listId && numAttributes === 1) {
    return ["list", { listId }];
  }

  if (listItemId && textId && numAttributes === 2) {
    return ["listItem", { listItemId, textId }];
  }

  if (listId && listItemId && numAttributes === 2) {
    return ["sublist", { listId, listItemId }];
  }

  if (tableId && numAttributes === 1) {
    return ["table", { tableId }];
  }

  if (tocId && numAttributes === 1) {
    return ["toc", { tocId }];
  }

  if (textId && numAttributes === 1) {
    return ["text", { textId }];
  }

  return ["hybrid", {}];
}

export function getRegionType(region: RegionTypeFragment): RegionType {
  const [regionType] = matchRegionType(region);
  return regionType;
}

export function getRegionName(fragment: RegionNameFragment): string {
  const [regionType] = matchRegionType(fragment);
  const regionName = i18next.t(`RegionName.${regionType}`);
  const regionHumanIndex = fragment.readingOrderIndex + 1;

  return `${regionName} [${regionHumanIndex}]`;
}
